import { DateTime } from 'luxon'
import { useContext } from 'react'

import type { AppDispatch } from '@redux/store'

import { generateRedirectUrl, launchModal } from '@domain-group/javascript-utils'
import { useForm, useLatestEntity } from '@domain-group/react-hooks'

import type { UseFormPayload as UseFormBasePayload, UseFormOptions } from '@domain-group/react-hooks'

import PageContext from '@contexts/pageContext'

import { setTimeToDayEnd } from '@functions/dateTime'
import mergeHashQueryParamsWithRedirect from '@functions/mergeHashQueryParamsWithRedirect'
import type { SubTaskType } from '@functions/taskListItem'

import useDispatch from '@hooks/useDispatch'
import useReduxAction from '@hooks/useReduxAction'
import useSelector from '@hooks/useSelector'

import * as taskListItemActions from '@redux/modules/taskListItem'

import defaultFormState, { requiredFields } from '@models/taskListItem'

import type { TaskListItemModel } from '@domain-group/realhub-backend-api-types'
import type { EntityOptions, TaskListItemModuleState } from '@redux/modules/types'

export const generateUrls = (
  taskListItem: Partial<TaskListItemModel> = {},
  options: { includeRedirect?: boolean } = {},
) => {
  const { includeRedirect } = options
  const { artwork_id, id, task_list_id } = taskListItem

  const redirect = includeRedirect ? `?redirect=${generateRedirectUrl()}` : ''

  return {
    editTaskListItemUrl: `#/projects/${task_list_id}/tasks/${id}/edit${redirect}`,
    taskListItemsIndexUrl: `#/tasks${redirect}`,
    testTaskListItemArtworkUrl: mergeHashQueryParamsWithRedirect({
      hash: `#/artworks/tasks/${id}/edit?artworkId=${artwork_id}&taskListItemId=${id}`,
      includeRedirect,
    }),
  }
}

type EntityActionFunctionParams = {
  dispatch: AppDispatch,
  entityParams: Partial<TaskListItemModel> & { dueDate?: string },
  requestOptions?: EntityOptions,
  taskListItem?: Partial<TaskListItemModel>,
}

const createTaskListItem = (params: EntityActionFunctionParams) => {
  const { entityParams, dispatch, requestOptions } = params
  const { createTaskListItem: createFn } = taskListItemActions

  const updatedParams = {
    ...entityParams,
    due_date: setTimeToDayEnd({ dateString: entityParams.due_date }),
  }

  // @ts-expect-error TS2345
  return dispatch(createFn(updatedParams, requestOptions))
}

export type BulkUpdateTaskListItems = {
  assigneeId: number,
  dueDate: string,
  taskListItemIds: number[],
}

type BulkUpdateParams = {
  dispatch: AppDispatch,
  entityParams: BulkUpdateTaskListItems,
  requestOptions?: EntityOptions,
}

const bulkUpdateTaskListItems = (params: BulkUpdateParams) => {
  const { entityParams, dispatch, requestOptions } = params
  const { bulkUpdate: bulkUpdateFn } = taskListItemActions

  const updatedParams = {
    ...entityParams,
    dueDate: setTimeToDayEnd({ dateString: entityParams.dueDate }),
  }

  // @ts-expect-error TS2345
  return dispatch(bulkUpdateFn(updatedParams, requestOptions))
}

const updateTaskListItem = (params: EntityActionFunctionParams) => {
  const { entityParams, dispatch, requestOptions } = params
  const { updateTaskListItem: updateFn } = taskListItemActions

  const updatedParams = {
    ...entityParams,
    due_date: setTimeToDayEnd({ dateString: entityParams.due_date }),
  }

  // @ts-expect-error TS2345
  return dispatch(updateFn(updatedParams, requestOptions))
}

const toggleCompletion = (params: EntityActionFunctionParams) => {
  const { dispatch, requestOptions, taskListItem } = params
  const { toggleTaskListItemCompletion: toggleCompletionFn } = taskListItemActions

  // @ts-expect-error TS2345
  return dispatch(toggleCompletionFn(taskListItem, requestOptions))
}

const deleteTaskListItem = (params: EntityActionFunctionParams) => {
  const { taskListItem, dispatch, requestOptions } = params
  const { deleteTaskListItem: deleteFn } = taskListItemActions

  // @ts-expect-error TS2554
  return dispatch(deleteFn(taskListItem, requestOptions))
}

const duplicateTaskListItem = (params: EntityActionFunctionParams) => {
  const { taskListItem, dispatch, requestOptions } = params
  // @ts-expect-error TS2339
  const { id: taskListItemId } = taskListItem

  const { duplicateTaskListItem: duplicateFn } = taskListItemActions

  return dispatch(duplicateFn(taskListItemId, requestOptions)).then((response) => {
    const { success } = response

    if (success){
      const { data } = response
      const { editTaskListItemUrl } = generateUrls(data)

      return {
        success,
        data,
        redirectUrl: editTaskListItemUrl,
      }
    }

    return response
  })
}

const setupTemplateTestingEntities = (params: EntityActionFunctionParams) => {
  const { dispatch, requestOptions, taskListItem } = params
  const { setupTemplateTestingEntities: setupFn } = taskListItemActions

  // @ts-expect-error TS2345
  return dispatch(setupFn(taskListItem, requestOptions))
}

const bulkAssignUserTasks = (params: { dispatch: AppDispatch, requestOptions: object }) => {
  const { dispatch, requestOptions } = params
  const { bulkAssignUserTasks: bulkAssignFn } = taskListItemActions

  return dispatch(bulkAssignFn(requestOptions))
}

type UseFormPayload = UseFormBasePayload & {
  creating?: boolean,
  updating?: boolean,
}

export function useTaskListItemForm(
  taskListItem: Partial<TaskListItemModel>,
  options?: UseFormOptions,
): UseFormPayload {
  const { validateOn } = options || {}

  const { creating, updating } = useSelector(reduxState => reduxState.taskListItems)

  // Map entity so we can set start and end dates
  // @ts-expect-error
  const dueDateString = taskListItem?.dates?.due?.date_time_with_timezone

  const entity = {
    ...taskListItem,
    due_date:
      taskListItem.id && dueDateString
        ? DateTime.fromISO(dueDateString).toFormat('yyyy-MM-dd')
        : taskListItem.due_date,
  }

  const taskListItemForm = useForm(
    defaultFormState,
    { entity, requiredFields: [...requiredFields], validateOn },
    [taskListItem.id, taskListItem.cache_key],
  )

  return {
    ...taskListItemForm,
    creating,
    updating,
  }
}

export const useRelations = (entity: Partial<TaskListItemModel> = {}) => {
  const {
    assignee_id,
    checklist_template_id,
    parent_task_id,
    status_id,
    task_list_id,
    task_list_section_id,
    user_id,
  } = entity

  const {
    statuses,
    taskListItems,
    taskListSections,
    taskListTemplates,
    taskLists,
    users,
  } = useSelector(reduxState => reduxState.entities)

  const assignee = assignee_id && users[assignee_id] ? users[assignee_id] : {}
  const checklistTemplate = checklist_template_id && taskListTemplates[checklist_template_id]
    ? taskListTemplates[checklist_template_id] : {}
  const parentTask = parent_task_id && taskListItems[parent_task_id] ? taskListItems[parent_task_id] : {}
  const status = status_id && statuses[status_id] ? statuses[status_id] : {}
  const taskList = task_list_id && taskLists[task_list_id] ? taskLists[task_list_id] : {}
  const taskListSection = task_list_section_id && taskListSections[task_list_section_id]
    ? taskListSections[task_list_section_id] : {}
  const user = user_id && users[user_id] ? users[user_id] : {}

  return {
    assignee,
    checklistTemplate,
    parentTask,
    status,
    taskList,
    taskListSection,
    user,
  }
}

interface UseTaskListItemOptions {
  performHttpRequests?: boolean,
  requestOptions?: EntityOptions,
}

function useTaskListItem(
  initEntity: Partial<TaskListItemModel> = {},
  options: UseTaskListItemOptions = {},
) {
  const { performHttpRequests, requestOptions } = options || {}

  const { entity: taskListItem } = useLatestEntity(initEntity, 'taskListItems')
  const {
    assignee_id,
    id,
    task_list_id,
    task_list_section_id,
  } = taskListItem

  const dispatch = useDispatch()

  const { callbacks } = useContext(PageContext)

  const {
    checklistTemplate, taskList, taskListSection, user,
  } = useRelations(taskListItem)

  const { creating, loading, updating } = useSelector(reduxState => reduxState.taskListItems)

  useReduxAction(
    'taskListItems',
    'loadTaskListItem',
    {
      ...requestOptions,
    },
    [id, performHttpRequests],
    {
      dispatchAction: (action, reqOptions: EntityOptions) => action(id, reqOptions),
      // @ts-expect-error
      shouldPerformFn: (entityReducer: TaskListItemModuleState) => {
        const { loadedIds } = entityReducer
        // @ts-expect-error TS2345
        return performHttpRequests && !loadedIds?.includes(id)
      },
    },
  )

  return {
    callbacks: {
      addSubTask: () => launchModal({
        callbacks,
        modalKey: 'CreateOrEditTaskListItemModal',
        payload: {
          taskListItem: {
            assignee_id,
            parent_task_id: id,
            task_list_id,
            task_list_section_id,
          },
        },
      }),
      bulkAssignUserTasks: (
        payload: { limit: number, task_types: SubTaskType[], user_id: number },
        entityRequestOptions: EntityOptions,
      ) => {
        const { limit, task_types, user_id } = payload

        return bulkAssignUserTasks({
          dispatch,
          requestOptions: {
            ...entityRequestOptions,
            limit,
            task_types,
            user_id,
          },
        })
      },
      createOrEditTaskListItem: () => launchModal({
        callbacks,
        modalKey: 'CreateOrEditTaskListItemModal',
        payload: {
          taskListItem,
        },
      }),
      createTaskListItem: (entityParams: Partial<TaskListItemModel>, entityRequestOptions: EntityOptions) => (
        createTaskListItem({ entityParams, dispatch, requestOptions: entityRequestOptions })
      ),
      deleteTaskListItem: (entityRequestOptions?: EntityOptions) => (
        // @ts-expect-error TS2322
        deleteTaskListItem({ dispatch, requestOptions: entityRequestOptions, taskListItem })
      ),
      bulkUpdateTaskListItems: (entityParams: BulkUpdateTaskListItems, entityRequestOptions?: EntityOptions) => (
        bulkUpdateTaskListItems({ entityParams, dispatch, requestOptions: entityRequestOptions })
      ),
      duplicateTaskListItem: (entityRequestOptions?: EntityOptions) => (
        // @ts-expect-error TS2322
        duplicateTaskListItem({ dispatch, requestOptions: entityRequestOptions, taskListItem })
      ),
      manageTaskListItems: (params: { taskListItemIds: number[], resetItems: () => void }) => launchModal({
        callbacks,
        modalKey: 'ManageTaskListItemsModal',
        payload: {
          ...params,
        },
      }),
      setupTemplateTestingEntities: (entityRequestOptions?: EntityOptions) => (
        // @ts-expect-error TS2322
        setupTemplateTestingEntities({ dispatch, requestOptions: entityRequestOptions, taskListItem })
      ),
      toggleCompletion: (entityRequestOptions?: EntityOptions) => (
        // @ts-expect-error TS2322
        toggleCompletion({ dispatch, requestOptions: entityRequestOptions, taskListItem })
      ),
      updateTaskListItem: (entityParams: Partial<TaskListItemModel>, entityRequestOptions?: EntityOptions) => (
        updateTaskListItem({ entityParams, dispatch, requestOptions: entityRequestOptions })
      ),
      viewChecklistTemplate: () => launchModal({
        callbacks,
        modalKey: 'ViewChecklistTemplateModal',
        payload: {
          taskListTemplate: checklistTemplate,
        },
      }),
    },
    creating,
    loading,
    taskList,
    taskListItem,
    taskListSection,
    updating,
    urls: generateUrls(taskListItem),
    user,
  }
}

export type UseTaskListItemPayload = ReturnType<typeof useTaskListItem>

export default useTaskListItem
